import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Collapse,
    Fab,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';

import useAuth from 'src/hooks/useAuth';
import { callApi } from 'src/utils/apiCall';
import { dispatch, useSelector } from 'src/redux/store';
import useIsVoicePracticeAdmin from 'src/pages/dashboard/practices/voice/useIsVoicePracticeAdmin';
import { updateError } from 'src/redux/slices/user';
import Iconify from 'src/components/Iconify';
import { useOutbound } from 'src/components/outbound/OutboundCallProvider';
import { isDiallable } from 'src/components/outbound/outboundModel';
import ElevateDialSection from 'src/components/outbound/ElevateDialSection';
import { DialerContext } from 'src/components/outbound/dialerContext';
import { fetchDialer } from 'src/components/outbound/outboundApi';
import {
    CHANNEL, DESK_STATE, DIALER_PLACEHOLDER, OPEN_DIALER_EVENT, ROUTE, callChannel, chooseChannel, deskStatus, leadIdFromPath, dialLeadId, dialNumber, dialerQuery, effectiveRoute,
    prefillView, readDialer, showChannelSwitch, usableChannels,
} from 'src/components/outbound/dialerModel';
import { CALL_PILL_ACTION_EVENT } from 'src/components/callPill/callPillFlag';
import { useCallFlow } from 'src/components/callFlow/CallFlowProvider';
import { ACTION, otherParty } from 'src/components/callPill/callPillModel';
import { CALL_FLOW_ASK_EVENT, askChoices, callerTitle } from 'src/components/callFlow/callFlowModel';
import { COULDNT_CONFIRM } from 'src/components/callPill/callOutcome';
import { formatUkPhone } from 'src/utils/ukPhone';
import {
    STREAMING_CHUNK_MS,
    appendPcm16Buffer,
    downsampleToPcm16,
    extractTranscriptEvent,
    takePcm16Chunks,
} from 'src/utils/liveCallTranscription';
import { VOICE_FAB_BOTTOM, VOICE_FAB_RIGHT } from './voiceFabLayout';
import DialerNoRouteHelp from './DialerNoRouteHelp';
import DialerOutcomeAsk from './DialerOutcomeAsk';
import DialerChannelSwitch from './DialerChannelSwitch';
import DialerWhatsappOption from './DialerWhatsappOption';
import { ukNumbersInText } from './dialerDisplay';
import { callLayer } from '../../components/callPill/callLayers';

const FEATURE_NAME = 'twilio-voice-calling';
const WHATSAPP_CALLING = 'whatsapp-calling';
const E164_RE = /^\+[1-9]\d{6,14}$/;
// The locked number reads as text: no box, normal text colour.
const LOCKED_NUMBER_SX = {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiInputBase-input': { fontWeight: 600, cursor: 'default' },
};
export const TWILIO_VOICE_CALL_ENDED_EVENT = 'twilio-voice-call-finished';

const TwilioVoiceContext = DialerContext;

function getApiResults(payload) {
    if (Array.isArray(payload)) return payload;
    return payload?.results || [];
}

function getResponseMessage(response, fallback) {
    return response?.data?.error || response?.data?.detail || fallback;
}

function getLeadName(lead) {
    if (!lead) return '';
    return [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
}

function getTwilioCallSid(call) {
    return call?.parameters?.CallSid || call?.outboundConnectionId || '';
}

function getTranscriptSpeakerLabel(speaker, patientLabel = 'Patient') {
    if (speaker === 'agent') return 'You';
    if (speaker === 'patient') return patientLabel || 'Patient';
    return 'Call';
}

function buildTranscriptText(rows, patientLabel) {
    return (rows || [])
        .filter((item) => item?.text)
        .map((item) => `${getTranscriptSpeakerLabel(item.speaker, patientLabel)}: ${item.text}`)
        .join('\n');
}

export function useTwilioVoice() {
    const context = useContext(TwilioVoiceContext);
    if (!context) {
        return {
            canUse: false,
            canOpen: false,
            isCalling: false,
            openDialer: () => {},
            startCall: () => {},
            hangup: () => {},
            toggleMute: () => {},
        };
    }
    return context;
}

export default function TwilioVoiceProvider({ children, practiceId }) {
    const theme = useTheme();
    const location = useLocation();
    // Hide the idle "New call" launcher FAB on the WhatsApp page — it overlaps the
    // composer send button. Routes look like /dashboard/practice/:id/whatsapp/...
    // Only the idle launcher is gated; an in-progress call widget stays visible.
    const isWhatsappRoute = (location?.pathname || '').includes('/whatsapp');
    const { user } = useAuth();
    const isVoiceAdmin = useIsVoicePracticeAdmin();
    // On a lead's page the floating button opens the dialer for that lead: the server fills in the number.
    const pageLeadId = leadIdFromPath(location?.pathname);
    const {
        permissions,
        permissionsLoading,
        permissionsPracticeId,
    } = useSelector((state) => ({
        permissions: state.permissions?.permissions,
        permissionsLoading: state.permissions?.loading,
        permissionsPracticeId: state.permissions?.practiceId,
    }));
    const permissionsReady = (
        !permissionsLoading
        && permissionsPracticeId != null
        && String(permissionsPracticeId) === String(practiceId)
    );
    // canUse = Twilio browser calling. The widget itself is the one dialer for
    // everyone who can call: Twilio users and Elevate desk callers (B6).
    const canUse = Boolean(user?.is_superuser) || (
        permissionsReady && permissions?.[FEATURE_NAME] === 'manage'
    );
    const outbound = useOutbound();
    const callFlow = useCallFlow();
    const callFlowRef = useRef(callFlow);
    callFlowRef.current = callFlow;
    const canCallElevate = Boolean(outbound?.canCall);
    const canOpen = canUse || canCallElevate;
    // The practice's WhatsApp calling (same gate as the WhatsApp chat's call button).
    const canWhatsappCall = Boolean(user?.is_superuser) || (
        permissionsReady && ['view', 'manage'].includes(permissions?.[WHATSAPP_CALLING])
    );

    const deviceRef = useRef(null);
    const devicePhoneIdRef = useRef(null);
    const activeCallRef = useRef(null);
    // The number the last call was dialled to: the call flow matches the end event to its waiting row by it.
    const dialledToRef = useRef('');
    // The last call whose end was announced (see resetCall).
    const endedCallRef = useRef(null);
    // The lead the last call was dialled for, so the finished-state question knows who it is about.
    const dialledLeadRef = useRef({ id: null, name: '' });
    const phonesRef = useRef([]);
    const phonesPracticeIdRef = useRef(null);
    const transcriberRefs = useRef({});
    const liveTranscriptRef = useRef([]);
    const callTranscriptMetaRef = useRef({
        callSid: '',
        callId: null,
        phoneId: null,
        patientLabel: '',
    });

    const [phones, setPhones] = useState([]);
    const [selectedPhoneId, setSelectedPhoneId] = useState('');
    const [panelOpen, setPanelOpen] = useState(false);
    const [dialValue, setDialValue] = useState('');
    const [activeLead, setActiveLead] = useState(null);
    const [activeLeadId, setActiveLeadId] = useState(null);
    const [activeCall, setActiveCall] = useState(null);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [isLoadingPhones, setIsLoadingPhones] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);
    const [liveTranscript, setLiveTranscript] = useState([]);
    const [partialTranscript, setPartialTranscript] = useState({});
    const [transcriptionError, setTranscriptionError] = useState('');
    // Route + pre-fill from voice-calls/dialer/ for the current open.
    const [dialer, setDialer] = useState(() => readDialer(null));
    const dialerSeqRef = useRef(0);
    // Call back: the channel the person called in on, and the one the user picked on the switch (CU-869fn2wcy).
    const [originChannel, setOriginChannel] = useState(null);
    const [pickedChannel, setPickedChannel] = useState(null);
    // The finished state: "Did they answer?" for a call we can't read the result of (CU-869fmc59c).
    // { call, canNotAPatient } while it is on screen.
    const [ask, setAsk] = useState(null);
    const [askBusy, setAskBusy] = useState(false);
    const [askLocked, setAskLocked] = useState(false);
    const [askMessage, setAskMessage] = useState('');

    const isCalling = Boolean(activeCall) || status === 'connecting';
    const transcriptRows = useMemo(() => liveTranscript.slice(-8), [liveTranscript]);

    const loadPhones = useCallback(async ({ force = false } = {}) => {
        if (!practiceId || !canUse) return [];
        if (
            !force
            && phonesRef.current.length
            && String(phonesPracticeIdRef.current) === String(practiceId)
        ) {
            return phonesRef.current;
        }

        setIsLoadingPhones(true);
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/`);
            if (!response?.ok && response?.status !== 200) {
                throw new Error(getResponseMessage(response, 'Could not load Twilio phone numbers.'));
            }
            const nextPhones = getApiResults(response.data).filter((phone) => phone.is_enabled !== false && phone.phone_number);
            phonesRef.current = nextPhones;
            phonesPracticeIdRef.current = practiceId;
            setPhones(nextPhones);
            setSelectedPhoneId((current) => {
                if (nextPhones.some((phone) => String(phone.id) === String(current))) return current;
                return nextPhones[0]?.id || '';
            });
            return nextPhones;
        } catch (err) {
            const message = err.message || 'Could not load Twilio phone numbers.';
            setError(message);
            dispatch(updateError({ success: false, message }));
            return [];
        } finally {
            setIsLoadingPhones(false);
        }
    }, [canUse, practiceId]);

    const fetchVoiceToken = useCallback(async (phoneId) => {
        const response = await callApi(
            `/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/voice-token/`,
            'post'
        );
        if (!response?.ok && response?.status !== 200) {
            throw new Error(getResponseMessage(response, 'Twilio Voice is not available for this number.'));
        }
        return response.data;
    }, [practiceId]);

    const destroyDevice = useCallback(() => {
        try {
            deviceRef.current?.destroy?.();
        } catch (_) {
            // Ignore SDK cleanup errors during route changes.
        }
        deviceRef.current = null;
        devicePhoneIdRef.current = null;
    }, []);

    const ensureDevice = useCallback(async (phoneId) => {
        if (deviceRef.current && String(devicePhoneIdRef.current) === String(phoneId)) {
            return deviceRef.current;
        }

        destroyDevice();
        const tokenPayload = await fetchVoiceToken(phoneId);
        const voiceSdk = await import('@twilio/voice-sdk');
        const VoiceDevice = voiceSdk.Device;
        const device = new VoiceDevice(tokenPayload.token, {
            closeProtection: true,
        });

        device.on('error', (sdkError) => {
            const message = sdkError?.message || 'Twilio Voice connection failed.';
            setStatus('error');
            setError(message);
            dispatch(updateError({ success: false, message }));
        });
        device.on('tokenWillExpire', async () => {
            try {
                const refreshed = await fetchVoiceToken(phoneId);
                device.updateToken(refreshed.token);
            } catch (err) {
                setError(err.message || 'Could not refresh Twilio Voice token.');
            }
        });

        deviceRef.current = device;
        devicePhoneIdRef.current = phoneId;
        return device;
    }, [destroyDevice, fetchVoiceToken]);

    const resetLiveTranscript = useCallback(() => {
        liveTranscriptRef.current = [];
        callTranscriptMetaRef.current = {
            callSid: '',
            callId: null,
            phoneId: null,
            patientLabel: '',
        };
        setLiveTranscript([]);
        setPartialTranscript({});
        setTranscriptionError('');
        setShowTranscript(false);
    }, []);

    const appendTranscript = useCallback((segment) => {
        setLiveTranscript((previous) => {
            const key = segment.provider_event_id || segment.providerEventId || segment.id || `${segment.speaker}-${segment.text}`;
            const next = [
                ...previous.filter((item) => (item.provider_event_id || item.providerEventId || item.id || `${item.speaker}-${item.text}`) !== key),
                segment,
            ].slice(-20);
            liveTranscriptRef.current = next;
            return next;
        });
    }, []);

    const fetchTranscriptionToken = useCallback(async (phoneId, callSid) => {
        const response = await callApi(
            `/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/live-transcription-token/`,
            'post',
            { call_sid: callSid || '' }
        );
        if (!response?.ok && response?.status !== 200) {
            throw new Error(getResponseMessage(response, 'Live transcription is unavailable.'));
        }
        return response.data;
    }, [practiceId]);

    const saveLiveTranscript = useCallback(async ({ phoneId, callSid, callId, transcript }) => {
        if (!practiceId || !phoneId || !transcript) return;
        const response = await callApi(
            `/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/live-transcript/`,
            'post',
            {
                call_sid: callSid || '',
                call_id: callId || undefined,
                transcript,
            }
        );
        if (!response?.ok && response?.status !== 200) {
            throw new Error(getResponseMessage(response, 'Could not save live transcript.'));
        }
    }, [practiceId]);

    const stopTranscriber = useCallback((speaker) => {
        const ref = transcriberRefs.current[speaker];
        if (!ref) return;
        try {
            ref.processor?.disconnect();
            ref.source?.disconnect();
            ref.silenceGain?.disconnect();
            ref.context?.close?.();
            if (ref.socket?.readyState === WebSocket.OPEN) {
                ref.socket.send(JSON.stringify({ type: 'ForceEndpoint' }));
                window.setTimeout(() => {
                    if (ref.socket?.readyState === WebSocket.OPEN) {
                        ref.socket.send(JSON.stringify({ type: 'Terminate' }));
                    }
                }, 250);
                window.setTimeout(() => {
                    if (ref.socket?.readyState !== WebSocket.CLOSED) ref.socket?.close?.();
                }, 1500);
            } else {
                ref.socket?.close?.();
            }
        } catch (_) {
            // Ignore teardown errors when a browser call is already closing.
        } finally {
            delete transcriberRefs.current[speaker];
            setPartialTranscript((previous) => {
                const next = { ...previous };
                delete next[speaker];
                return next;
            });
        }
    }, []);

    const stopAllTranscribers = useCallback(() => {
        Object.keys(transcriberRefs.current || {}).forEach(stopTranscriber);
    }, [stopTranscriber]);

    const getCallStream = useCallback((call, speaker) => (
        speaker === 'agent'
            ? call?.getLocalStream?.()
            : call?.getRemoteStream?.()
    ), []);

    const startTranscriber = useCallback(async (call, phoneId, speaker, attempt = 0) => {
        if (!call || !phoneId || transcriberRefs.current[speaker] || typeof WebSocket === 'undefined') return;
        const stream = getCallStream(call, speaker);
        if (!stream) {
            if (attempt < 8) {
                window.setTimeout(() => startTranscriber(call, phoneId, speaker, attempt + 1), 250);
            }
            return;
        }

        try {
            const callSid = getTwilioCallSid(call);
            const tokenPayload = await fetchTranscriptionToken(phoneId, callSid);
            if (!tokenPayload?.token) {
                setTranscriptionError('Live transcript is unavailable.');
                return;
            }
            callTranscriptMetaRef.current = {
                callSid,
                callId: tokenPayload.call_id || callTranscriptMetaRef.current.callId || null,
                phoneId,
                patientLabel: callTranscriptMetaRef.current.patientLabel,
            };

            const resolvedSampleRate = tokenPayload.sample_rate || 16000;
            const query = new URLSearchParams({
                token: tokenPayload.token,
                sample_rate: String(resolvedSampleRate),
                encoding: tokenPayload.encoding || 'pcm_s16le',
                speech_model: tokenPayload.speech_model || 'u3-rt-pro',
                mode: 'balanced',
                format_turns: String(tokenPayload.format_turns ?? true),
            });
            const socket = new WebSocket(`${tokenPayload.websocket_url}?${query.toString()}`);
            socket.binaryType = 'arraybuffer';

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const context = new AudioContextClass();
            const source = context.createMediaStreamSource(stream);
            const processor = context.createScriptProcessor(4096, 1, 1);
            const silenceGain = context.createGain();
            silenceGain.gain.value = 0;
            const samplesPerChunk = Math.max(1, Math.round((resolvedSampleRate * STREAMING_CHUNK_MS) / 1000));
            let pendingPcm = new Int16Array(0);

            source.connect(processor);
            processor.connect(silenceGain);
            silenceGain.connect(context.destination);

            processor.onaudioprocess = (event) => {
                if (socket.readyState !== WebSocket.OPEN) return;
                const pcm = downsampleToPcm16(
                    event.inputBuffer.getChannelData(0),
                    context.sampleRate,
                    resolvedSampleRate
                );
                pendingPcm = appendPcm16Buffer(pendingPcm, pcm);
                const { chunks, remaining } = takePcm16Chunks(pendingPcm, samplesPerChunk);
                pendingPcm = remaining;
                chunks.forEach((chunk) => socket.send(chunk));
            };

            socket.onmessage = (event) => {
                try {
                    const transcriptEvent = extractTranscriptEvent(JSON.parse(event.data));
                    if (!transcriptEvent) return;
                    if (!transcriptEvent.isFinal) {
                        setPartialTranscript((previous) => ({ ...previous, [speaker]: transcriptEvent.text }));
                        return;
                    }

                    const segment = {
                        speaker,
                        text: transcriptEvent.text,
                        is_final: true,
                        started_at_ms: transcriptEvent.startedAtMs,
                        ended_at_ms: transcriptEvent.endedAtMs,
                        provider_event_id: transcriptEvent.providerEventId !== ''
                            ? `${speaker}-${transcriptEvent.providerEventId}`
                            : `${speaker}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    };
                    setPartialTranscript((previous) => {
                        const next = { ...previous };
                        delete next[speaker];
                        return next;
                    });
                    appendTranscript(segment);
                } catch (_) {
                    // Ignore malformed upstream transcript frames.
                }
            };

            socket.onerror = () => {
                setTranscriptionError('Live transcript had a connection issue.');
            };

            transcriberRefs.current[speaker] = { socket, context, source, processor, silenceGain };
            context.resume?.().catch(() => {});
        } catch (err) {
            setTranscriptionError(err.message || 'Live transcript is unavailable.');
        }
    }, [
        appendTranscript,
        fetchTranscriptionToken,
        getCallStream,
    ]);

    const startLiveTranscription = useCallback((call, phoneId) => {
        if (!call || !phoneId) return;
        setTranscriptionError('');
        setShowTranscript(true);
        startTranscriber(call, phoneId, 'agent');
        startTranscriber(call, phoneId, 'patient');
    }, [startTranscriber]);

    const finalizeLiveTranscript = useCallback((call, phoneId) => {
        stopAllTranscribers();
        const patientLabel = callTranscriptMetaRef.current.patientLabel || 'Patient';
        const transcript = buildTranscriptText(liveTranscriptRef.current, patientLabel);
        const meta = callTranscriptMetaRef.current;
        const callSid = meta.callSid || getTwilioCallSid(call);
        const targetPhoneId = phoneId || meta.phoneId || selectedPhoneId;
        if (transcript && targetPhoneId && (callSid || meta.callId)) {
            return saveLiveTranscript({
                phoneId: targetPhoneId,
                callSid,
                callId: meta.callId,
                transcript,
            }).catch(() => {});
        }
        return Promise.resolve();
    }, [saveLiveTranscript, selectedPhoneId, stopAllTranscribers]);

    const resetCall = useCallback((phoneId, call = null) => {
        const finishedCall = call || activeCallRef.current;
        // Each call ends once: Hang up fires the SDK's 'disconnect' (and then resets again itself), and
        // cancel / reject can follow. Only the first reset of a call saves the transcript and announces the end.
        const alreadyEnded = Boolean(finishedCall) && endedCallRef.current === finishedCall;
        if (finishedCall) endedCallRef.current = finishedCall;
        const transcriptSave = alreadyEnded ? Promise.resolve() : finalizeLiveTranscript(finishedCall, phoneId);
        activeCallRef.current = null;
        setActiveCall(null);
        setStatus('idle');
        setIsMuted(false);
        const eventPhoneId = phoneId || selectedPhoneId;
        // Only a call this widget placed counts as "a call-back ended"; a ringing incoming call that
        // is rejected or cancelled is not one.
        const incoming = finishedCall?.direction === 'INCOMING';
        if (practiceId && eventPhoneId && !incoming && finishedCall && !alreadyEnded) {
            Promise.resolve(transcriptSave).finally(() => {
                window.dispatchEvent(new CustomEvent(TWILIO_VOICE_CALL_ENDED_EVENT, {
                    detail: {
                        practiceId, phoneId: eventPhoneId, to: dialledToRef.current, leadId: dialledLeadRef.current.id, leadName: dialledLeadRef.current.name,
                    },
                }));
            });
        }
    }, [finalizeLiveTranscript, practiceId, selectedPhoneId]);

    const attachCallListeners = useCallback((call, phoneId) => {
        call.on('accept', () => {
            setStatus('connected');
            setError('');
            startLiveTranscription(call, phoneId);
        });
        call.on('disconnect', () => resetCall(phoneId, call));
        call.on('cancel', () => resetCall(phoneId, call));
        call.on('reject', () => resetCall(phoneId, call));
        call.on('error', (sdkError) => {
            const message = sdkError?.message || 'Call failed.';
            setStatus('error');
            setError(message);
            dispatch(updateError({ success: false, message }));
        });
    }, [resetCall, startLiveTranscription]);

    const startCall = useCallback(async ({ to, leadId, lead, phoneId } = {}) => {
        if (!practiceId || !canUse || isCalling) return;

        const availablePhones = await loadPhones();
        const nextPhoneId = phoneId || selectedPhoneId || availablePhones[0]?.id;
        const destination = (to || dialValue || '').trim();

        if (!nextPhoneId) {
            dispatch(updateError({ success: false, message: 'No Twilio phone number is configured.' }));
            return;
        }
        if (!destination && !leadId) {
            setError('Enter a phone number to call.');
            return;
        }
        if (!leadId && !E164_RE.test(destination)) {
            setError('Enter a valid E.164 phone number.');
            return;
        }

        setError('');
        dialledToRef.current = destination;
        dialledLeadRef.current = { id: leadId || activeLeadId || null, name: getLeadName(lead || activeLead) };
        setAsk(null);
        resetLiveTranscript();
        callTranscriptMetaRef.current.patientLabel = getLeadName(lead || activeLead) || destination || 'Patient';
        setStatus('connecting');
        setSelectedPhoneId(nextPhoneId);
        setActiveLead(lead || activeLead || null);
        setActiveLeadId(leadId || activeLeadId || null);
        setPanelOpen(true);

        try {
            const device = await ensureDevice(nextPhoneId);
            const call = await device.connect({
                params: {
                    To: destination,
                    LeadId: leadId ? String(leadId) : '',
                },
            });
            activeCallRef.current = call;
            setActiveCall(call);
            attachCallListeners(call, nextPhoneId);
        } catch (err) {
            const message = err.message || 'Could not start the call.';
            setStatus('error');
            setError(message);
            dispatch(updateError({ success: false, message }));
        }
    }, [
        activeLead,
        activeLeadId,
        attachCallListeners,
        canUse,
        dialValue,
        ensureDevice,
        isCalling,
        loadPhones,
        practiceId,
        resetLiveTranscript,
        selectedPhoneId,
    ]);

    // Every Call button opens this widget pre-filled; the backend says whether
    // it rings the user's Elevate desk or calls through Twilio (contract V2-3).
    const openDialer = useCallback(({ to = '', leadId = null, lead = null, phoneId = null, callId = null, provider = null } = {}) => {
        if (!canOpen || !practiceId || isCalling) return false;
        const typed = to || '';
        setAsk(null);
        setOriginChannel(callChannel({ callId, provider }));
        setPickedChannel(null);
        setDialValue(typed);
        setActiveLead(lead);
        setActiveLeadId(leadId);
        setError('');
        if (phoneId) setSelectedPhoneId(phoneId);
        setPanelOpen(true);
        const flags = { canCallElevate, canUseTwilio: canUse };
        const seq = dialerSeqRef.current + 1;
        dialerSeqRef.current = seq;
        setDialer({ ...readDialer(null, flags), loading: true });
        if (canUse) loadPhones();
        fetchDialer(practiceId, dialerQuery({ leadId, callId, to })).then((res) => {
            if (dialerSeqRef.current !== seq) return;
            const next = readDialer(res, flags);
            setDialer(next);
            // Show the readable number unless the user has typed since.
            if (next.prefill.number_display) setDialValue((prev) => (prev === typed ? next.prefill.number_display : prev));
        });
        return true;
    }, [canOpen, canCallElevate, canUse, isCalling, loadPhones, practiceId]);

    const closeDialer = useCallback(() => {
        callFlowRef.current.forgetAsk?.();
        dialerSeqRef.current += 1;
        setPanelOpen(false);
        setAsk(null);
        setAskBusy(false);
        setAskLocked(false);
        setAskMessage('');
    }, []);

    // The call flow asks "Did they answer?" here (a call placed on a handset, through the browser
    // widget, or "I've called them" on a call-back row). Cancelling the event tells it this widget took the question.
    useEffect(() => {
        if (!canOpen || !practiceId) return undefined;
        const onAsk = (e) => {
            const { call, canNotAPatient } = e.detail || {};
            if (!call || isCalling) return;
            dialerSeqRef.current += 1;
            setAsk({ call, canNotAPatient: Boolean(canNotAPatient) });
            setAskBusy(false);
            setAskLocked(false);
            setAskMessage('');
            setPanelOpen(true);
            e.preventDefault();
        };
        window.addEventListener(CALL_FLOW_ASK_EVENT, onAsk);
        return () => window.removeEventListener(CALL_FLOW_ASK_EVENT, onAsk);
    }, [canOpen, isCalling, practiceId]);

    const chooseAsk = async (choice) => {
        if (!ask || askBusy || askLocked) return;
        setAskBusy(true);
        setAskMessage('');
        const result = await callFlow.answerAsk(ask.call, choice);
        setAskBusy(false);
        if (result?.ok) { closeDialer(); return; }
        // No reply / 5xx: the answer may have been saved, so lock the buttons instead of inviting a retry.
        if (result?.uncertain) { setAskLocked(true); setAskMessage(COULDNT_CONFIRM); return; }
        setAskMessage(result?.message || 'The outcome could not be saved.');
    };

    const hangup = useCallback(() => {
        try {
            activeCallRef.current?.disconnect?.();
            deviceRef.current?.disconnectAll?.();
        } finally {
            resetCall(selectedPhoneId);
        }
    }, [resetCall, selectedPhoneId]);

    const toggleMute = useCallback(() => {
        if (!activeCallRef.current) return;
        const nextMuted = !isMuted;
        activeCallRef.current.mute(nextMuted);
        setIsMuted(nextMuted);
    }, [isMuted]);

    useEffect(() => {
        if (!practiceId || !canUse) {
            phonesRef.current = [];
            phonesPracticeIdRef.current = null;
            setPhones([]);
            setSelectedPhoneId('');
            stopAllTranscribers();
            destroyDevice();
            return;
        }
        loadPhones({ force: true });
    }, [canUse, destroyDevice, loadPhones, practiceId, stopAllTranscribers]);

    // A practice switch, or losing the right to call, closes the widget.
    useEffect(() => {
        if (!practiceId || !canOpen) closeDialer();
    }, [canOpen, closeDialer, practiceId]);
    useEffect(() => { closeDialer(); }, [closeDialer, practiceId]);

    // Other screens open the widget by event (dialerModel.requestDialer), and
    // "Call back" on a missed-call pill opens it pre-filled instead of Call Logs.
    useEffect(() => {
        if (!canOpen) return undefined;
        const onOpen = (e) => {
            const { to, leadId, lead, callId, provider } = e.detail || {};
            if (openDialer({ to, leadId, lead, callId, provider })) e.preventDefault();
        };
        const onPillAction = (e) => {
            const { action, call } = e.detail || {};
            // Withheld callers keep the pill's own default (Call Logs).
            if (action !== ACTION.CALL_BACK || !call || e.defaultPrevented || !isDiallable(otherParty(call))) return;
            // Same as Call back on a row: the call flow claims, opens only this dialer, and the question
            // "Did they answer?" is asked here when the call ends. (The widget cannot open: leave the pill's default.)
            if (!canOpen || isCalling) return;
            e.preventDefault();
            callFlow.openCallBack(call);
        };
        window.addEventListener(OPEN_DIALER_EVENT, onOpen);
        window.addEventListener(CALL_PILL_ACTION_EVENT, onPillAction);
        return () => {
            window.removeEventListener(OPEN_DIALER_EVENT, onOpen);
            window.removeEventListener(CALL_PILL_ACTION_EVENT, onPillAction);
        };
    }, [callFlow, canOpen, isCalling, openDialer]);

    useEffect(() => () => {
        stopAllTranscribers();
        destroyDevice();
    }, [destroyDevice, stopAllTranscribers]);

    const selectedPhone = useMemo(
        () => phones.find((phone) => String(phone.id) === String(selectedPhoneId)) || null,
        [phones, selectedPhoneId]
    );

    const contextValue = useMemo(() => ({
        canUse,
        canOpen,
        isCalling,
        openDialer,
        startCall,
        hangup,
        toggleMute,
        selectedPhoneId,
        phones,
        status,
    }), [canUse, canOpen, hangup, isCalling, openDialer, phones, selectedPhoneId, startCall, status, toggleMute]);

    const leadName = dialer.prefill.lead?.name || getLeadName(activeLead);
    const statusLabel = useMemo(() => {
        if (status === 'connected') return 'Connected';
        if (status === 'connecting') return 'Connecting';
        return formatUkPhone(selectedPhone?.phone_number) || 'Select caller ID';
    }, [selectedPhone, status]);
    const asking = Boolean(ask) && !isCalling;
    const { route } = dialer;
    const elevateDeskState = deskStatus(outbound?.desk || null).state;
    const serverRoute = effectiveRoute({ route, canCallElevate, canUseTwilio: canUse, deskState: elevateDeskState });
    // Call back on the channel they called in on, when this user can use it; the switch changes it.
    const personLeadId = activeLeadId ?? dialer.prefill.lead?.id ?? null;
    const channels = usableChannels({
        route, canCallElevate, canUseTwilio: canUse, twilioNumbers: phones.length, canWhatsappCall, hasPerson: personLeadId != null || originChannel != null,
    });
    const chosen = chooseChannel({ picked: pickedChannel, origin: originChannel, fallback: serverRoute, usable: channels, deskState: elevateDeskState });
    const isWhatsapp = chosen.channel === CHANNEL.WHATSAPP && !isCalling;
    let usedRoute = chosen.channel || ROUTE.NONE;
    if (isWhatsapp) usedRoute = ROUTE.NONE;
    const isElevate = usedRoute === ROUTE.ELEVATE && !isCalling;
    const isTwilio = usedRoute === ROUTE.TWILIO || (canUse && isCalling);
    // Server said Elevate but this desk can't ring: we're on Twilio instead.
    const twilioInsteadOfDesk = !isCalling && chosen.why === 'server' && route === ROUTE.ELEVATE && usedRoute === ROUTE.TWILIO;
    const view = prefillView(dialer.prefill, { to: dialValue, lead: activeLead });
    const numberToDial = dialNumber(dialer.prefill, dialValue);
    // The lead's stored number can't be dialled and nobody has typed another one yet (CU-869fnwgxf).
    const showNumberProblem = Boolean(view.numberProblem) && numberToDial === (dialer.prefill.number || '');
    // Header text, numbers read the way staff say them (display only; the dialled value is untouched).
    let headerTitle = ukNumbersInText(view.title);
    let headerDetails = isTwilio ? statusLabel : ukNumbersInText(view.details.join(' · '));
    if (isCalling) headerTitle = leadName || formatUkPhone(dialValue) || 'New call';
    if (asking) { headerTitle = callerTitle(ask.call, formatUkPhone); headerDetails = 'Call finished'; }
    const elevateDesk = outbound?.desk || null;
    const elevateHandset = isElevate && elevateDeskState === DESK_STATE.HANDSET;
    const leadIdToDial = dialLeadId(dialer.prefill, activeLeadId, numberToDial);
    const numberLocked = Boolean(activeLeadId);
    const placeCall = () => {
        if (isElevate) {
            // Rings this computer's desk (or asks which desk it is first); the
            // call pill takes over from here.
            outbound.startCall({ to: numberToDial, leadId: leadIdToDial });
            closeDialer();
            return;
        }
        startCall({ to: numberToDial, leadId: leadIdToDial, lead: activeLead, phoneId: selectedPhoneId });
    };

    return (
        <TwilioVoiceContext.Provider value={contextValue}>
            {children}

            {canOpen && practiceId && !panelOpen && !isCalling && !isWhatsappRoute && (
                <Tooltip title="New call" arrow>
                    <Fab
                        color="primary"
                        size="medium"
                        onClick={() => openDialer({ leadId: pageLeadId })}
                        aria-label="New call"
                        sx={{
                            position: 'fixed',
                            right: VOICE_FAB_RIGHT,
                            bottom: VOICE_FAB_BOTTOM,
                            zIndex: callLayer('DIALER_BUTTON')(theme),
                            boxShadow: theme.customShadows?.primary || theme.shadows[8],
                        }}
                    >
                        <Iconify icon="solar:phone-calling-rounded-bold" width={22} />
                    </Fab>
                </Tooltip>
            )}

            {canOpen && practiceId && (panelOpen || isCalling) && (
                <Card
                    sx={{
                        position: 'fixed',
                        right: { xs: 12, md: 24 },
                        bottom: { xs: 12, md: 24 },
                        zIndex: callLayer('DIALER')(theme),
                        width: { xs: 'calc(100% - 24px)', sm: 360 },
                        p: 2,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.grey[500], 0.16),
                        boxShadow: theme.customShadows?.dropdown || theme.shadows[16],
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1.25,
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: 'primary.main',
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Iconify icon="solar:phone-calling-rounded-bold" width={18} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle2" noWrap data-testid="dialer-title">
                                    {headerTitle}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap component="div" data-testid="dialer-details">
                                    {headerDetails}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={closeDialer}
                                disabled={isCalling}
                                aria-label="Close dialer"
                            >
                                <Iconify icon="eva:close-fill" width={18} />
                            </IconButton>
                        </Stack>

                        {error && <Alert severity="error">{error}</Alert>}

                        {asking && (
                            <DialerOutcomeAsk
                                choices={askChoices({ canNotAPatient: ask.canNotAPatient })}
                                busy={askBusy}
                                locked={askLocked}
                                message={askMessage}
                                onChoose={chooseAsk}
                            />
                        )}

                        {!asking && <>
                        {!isCalling && (view.notes.length > 0 || view.archived || showNumberProblem) && (
                            <Stack spacing={0.25} data-testid="dialer-notes">
                                {view.archived && <Typography variant="caption" color="warning.main">Archived patient</Typography>}
                                {showNumberProblem && (
                                    <Typography variant="caption" color="warning.dark" data-testid="dialer-number-problem">
                                        {`Can't call the number on record: ${view.numberProblem} Check it with the patient, or type the right number.`}
                                    </Typography>
                                )}
                                {view.notes.map((note) => (
                                    <Typography key={note} variant="caption" color="text.secondary">{note}</Typography>
                                ))}
                            </Stack>
                        )}

                        {dialer.loading && !isCalling && <Typography variant="caption" color="text.disabled">Loading call details…</Typography>}

                        {!dialer.loading && !isCalling && showChannelSwitch(channels, chosen.channel) && (
                            <DialerChannelSwitch options={channels} value={chosen.channel} onChange={setPickedChannel} />
                        )}

                        {!dialer.loading && isWhatsapp && (
                            <DialerWhatsappOption
                                practiceId={practiceId}
                                leadId={personLeadId}
                                archived={view.archived}
                                onOpened={closeDialer}
                            />
                        )}

                        {!dialer.loading && usedRoute === ROUTE.NONE && !isCalling && !isWhatsapp && (
                            <DialerNoRouteHelp
                                canCallElevate={canCallElevate}
                                isVoiceAdmin={isVoiceAdmin}
                                reason={dialer.reason}
                                practiceId={practiceId}
                                callId={dialer.prefill.call?.id}
                                onChooseDesk={() => outbound.openDeskPicker()}
                                onCalledThem={() => callFlow.askAboutCall({ callId: dialer.prefill.call?.id, number: numberToDial, leadId: dialer.prefill.lead?.id ?? null, leadName: dialer.prefill.lead?.name || '' })}
                            />
                        )}

                        {isElevate && (
                            <ElevateDialSection
                                desk={elevateDesk}
                                number={numberToDial}
                                onChangeDesk={() => outbound.openDeskPicker()}
                                noDesks={Boolean(outbound?.canPickDesk) && !(outbound?.desks || []).length}
                                isVoiceAdmin={isVoiceAdmin}
                            />
                        )}

                        {twilioInsteadOfDesk && (
                            <Typography variant="caption" color="text.secondary" data-testid="dialer-twilio-instead">
                                Your desk can&apos;t be rung from the CRM, so this call goes through Twilio.
                            </Typography>
                        )}

                        {isTwilio && (
                        <Select
                            size="small"
                            // Its menu opens above the dialer panel (callLayers.js).
                            MenuProps={{ sx: { zIndex: callLayer('POPUP') } }}
                            value={selectedPhoneId || ''}
                            onChange={(event) => setSelectedPhoneId(event.target.value)}
                            disabled={isCalling || isLoadingPhones || phones.length <= 1}
                            displayEmpty
                        >
                            {phones.length ? phones.map((phone) => (
                                <MenuItem key={phone.id} value={phone.id}>
                                    {formatUkPhone(phone.phone_number) || `Phone ${phone.id}`}
                                </MenuItem>
                            )) : (
                                <MenuItem value="" disabled>
                                    {isLoadingPhones ? 'Loading numbers' : 'No Twilio numbers'}
                                </MenuItem>
                            )}
                        </Select>
                        )}

                        {!isWhatsapp && <>
                        <TextField
                            size="small"
                            label={numberLocked ? 'Calling' : 'Phone number'}
                            value={dialValue}
                            onChange={(event) => setDialValue(event.target.value)}
                            // A lead's number is locked: shown as read-only text, not a greyed-out field (CU-869fn2wd3).
                            disabled={isCalling && !numberLocked}
                            placeholder={DIALER_PLACEHOLDER}
                            InputProps={{
                                readOnly: numberLocked,
                                inputProps: { 'data-testid': 'dialer-number' },
                                startAdornment: numberLocked ? (
                                    <Iconify icon="solar:lock-keyhole-minimalistic-bold" width={16} sx={{ mr: 1, color: 'text.secondary' }} />
                                ) : (
                                    <Iconify icon="solar:phone-bold-duotone" width={18} sx={{ mr: 1, color: 'text.disabled' }} />
                                ),
                            }}
                            sx={numberLocked ? LOCKED_NUMBER_SX : undefined}
                        />

                        <Stack direction="row" spacing={1}>
                            {isCalling ? (
                                <>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color={isMuted ? 'warning' : 'inherit'}
                                        onClick={toggleMute}
                                        startIcon={<Iconify icon={isMuted ? 'solar:muted-bold' : 'solar:microphone-3-bold'} />}
                                    >
                                        {isMuted ? 'Unmute' : 'Mute'}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="error"
                                        onClick={hangup}
                                        startIcon={<Iconify icon="solar:end-call-bold" />}
                                    >
                                        Hang up
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={placeCall}
                                    disabled={isElevate
                                        ? (dialer.loading || elevateHandset || outbound?.busy || !isDiallable(numberToDial))
                                        : (dialer.loading || !isTwilio || status === 'connecting' || isLoadingPhones || (!dialValue && !activeLeadId))}
                                    data-testid="dialer-call"
                                    startIcon={status === 'connecting' ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="solar:phone-calling-rounded-bold" />}
                                >
                                    Call
                                </Button>
                            )}
                        </Stack>
                        </>}

                        </>}

                        {/* The error and the finished call's transcript stay visible while the question shows. */}
                        {(isCalling || transcriptRows.length > 0 || transcriptionError) && (
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Button
                                    size="small"
                                    variant={showTranscript ? 'contained' : 'outlined'}
                                    onClick={() => setShowTranscript((value) => !value)}
                                    startIcon={<Iconify icon="solar:document-text-outline" width={16} />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Live transcript
                                </Button>
                                {transcriptionError && (
                                    <Typography variant="caption" color="warning.main" noWrap>
                                        {transcriptionError}
                                    </Typography>
                                )}
                            </Stack>
                        )}

                        <Collapse in={showTranscript && (isCalling || transcriptRows.length > 0 || Object.values(partialTranscript).some(Boolean))}>
                            <Box
                                sx={{
                                    maxHeight: 190,
                                    overflowY: 'auto',
                                    p: 1.25,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.grey[500], 0.06),
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.grey[500], 0.12),
                                }}
                            >
                                {transcriptRows.length === 0 && !Object.values(partialTranscript).some(Boolean) ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Live transcript will appear here.
                                    </Typography>
                                ) : (
                                    <Stack spacing={1}>
                                        {transcriptRows.map((item) => (
                                            <Box
                                                key={item.provider_event_id || item.providerEventId || item.id || `${item.speaker}-${item.text}`}
                                                sx={{ minWidth: 0 }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        fontWeight: 700,
                                                        color: 'text.secondary',
                                                        textTransform: 'uppercase',
                                                        lineHeight: 1.2,
                                                        fontSize: '0.65rem',
                                                    }}
                                                >
                                                    {getTranscriptSpeakerLabel(item.speaker, leadName || dialValue || 'Patient')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.45 }}>
                                                    {item.text}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {Object.entries(partialTranscript).map(([speaker, text]) => text ? (
                                            <Box key={`partial-${speaker}`} sx={{ minWidth: 0, opacity: 0.72 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        fontWeight: 700,
                                                        color: 'text.secondary',
                                                        textTransform: 'uppercase',
                                                        lineHeight: 1.2,
                                                        fontSize: '0.65rem',
                                                    }}
                                                >
                                                    {getTranscriptSpeakerLabel(speaker, leadName || dialValue || 'Patient')}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.45 }}>
                                                    {text}
                                                </Typography>
                                            </Box>
                                        ) : null)}
                                    </Stack>
                                )}
                            </Box>
                        </Collapse>
                    </Stack>
                </Card>
            )}
        </TwilioVoiceContext.Provider>
    );
}

TwilioVoiceProvider.propTypes = {
    children: PropTypes.node,
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
